import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { createSelector, select, Store } from "@ngrx/store";
import { AppState } from "../store";
import {
  arePricingPlansReady,
  isLoggedIn,
  isLoggedOut,
  isTeamManager,
  selectTenantInfo,
  selectUser,
  selectUserPermissions,
  userPictureUrl,
} from "../store/selectors";
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from "@angular/router";
import { environment } from "../../environments/environment";
import { TenantService } from "../services/tenant.service";
import {
  checkIfPlatformSite,
  checkIfSingleSignOnPage,
  goToLoginPage,
} from "../common/platform-utils";
import { isAnonymousUser, User } from "../models/user.model";
import { MatDialog } from "@angular/material/dialog";
import { NewsletterDialogComponent } from "../newsletter-dialog/newsletter-dialog.component";
import { UiEventsService } from "../services/ui-events.service";
import { MenuEntry, TenantInfo } from "../models/tenant.model";
import { createDefaultDialogConfig } from "../common/dialog-utils";
import { LoadingService } from "../shared/services/loading.service";
import { ResponsiveLayoutService } from "../services/responsive-layout.service";
import { TranslateModule } from "@ngx-translate/core";
import { AllowRoleDirective } from "../shared/auth/allow-role.directive";
import { AllowPlanDirective } from "../shared/allow-plan/allow-plan.directive";
import { DenyRoleDirective } from "../shared/auth/deny-role.directive";
import { AiGlobalChatService } from "../ai-global-chat/ai-global-chat.service";
import { MessagesService } from "../shared/services/messages.service";
import { TrialExpirationComponent } from "../shared/trial-expiration/trial-expiration.component";
import { Auth } from "@angular/fire/auth";
import { UserPermissions } from "../models/user-permissions.model";
import { TopMenuSvgSpriteComponent } from "./top-menu-svg-sprite.component";
import { NotificationDialogComponent } from "./notification-dialog/notification-dialog.component";
import { loadStripeJs } from "../shared/utils/stripe-loader";
import { UserNotificationsService } from "../services/user-notifications.service";
import { userDetailsUpdated } from "../store/user.actions";
import { MatMenuModule } from "@angular/material/menu";
import { MatButtonModule } from "@angular/material/button";
import { SitePagesService } from "../page-builder/site-pages/services/site-pages.service";
import { CustomPage } from "../models/page-builder/custom-page.model";
import { isLifetimeCustomer } from '../common/appsumo-utils';
import { createInstructorPlanDialog } from '../admin/school-settings/change-instructor-plan-dialog/change-instructor-plan-dialog.component';
import { MatTooltipModule } from "@angular/material/tooltip";
import { toSignal } from '@angular/core/rxjs-interop';
import { concatMap, filter, map, tap } from 'rxjs/operators';
import { InstructorPlanService } from '../admin/school-settings/instructor-plan.service';
import { GlobalAiIconSvgComponent } from '../svg-sprites/general/global-ai-icon-svg.component';
import { UserActivityLogService } from '../services/user-activity-log.service';
import { FeatureFlagService } from '../shared/services/feature-flag.service';
import { CookieService } from 'ngx-cookie-service';
import { UserPermissionsService } from '../shared/services/user-permissions.service';
import { PermissionKeys } from '../shared/services/permissions-key.enum';
import { CloseIconSvgComponent } from '../svg-sprites/general/close-icon-svg.component';

interface TopMenuData {
  tenant: TenantInfo;
  isLoggedIn: boolean;
  isLoggedOut: boolean;
  pictureUrl: string;
  arePricingPlansReady: boolean;
  user: User;
  showSubscription: boolean;
  isTeamManager: boolean;
  permissions: UserPermissions;
}

const selectTopMenuData = createSelector(
  selectTenantInfo,
  isLoggedIn,
  isLoggedOut,
  userPictureUrl,
  arePricingPlansReady,
  selectUser,
  isTeamManager,
  selectUserPermissions,
  (tenant, isLoggedIn, isLoggedOut, pictureUrl, arePricingPlansReady, user, isTeamManager, permissions) => ({
    tenant,
    isLoggedIn,
    isLoggedOut,
    pictureUrl,
    arePricingPlansReady,
    user,
    isTeamManager,
    showSubscription: permissions.isAdmin || (arePricingPlansReady && tenant?.isConnectedToStripe),
    permissions,
  })
);

@Component({
  selector: "top-menu",
  templateUrl: "./top-menu.component.html",
  styleUrls: ["./top-menu.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:popstate)': 'onPopState()',
    '(document:click)': 'clickout($event)',
    '(window:resize)': 'onResize()',
  },
  imports: [
    RouterLink,
    RouterLinkActive,
    DenyRoleDirective,
    AllowPlanDirective,
    AllowRoleDirective,
    TranslateModule,
    TrialExpirationComponent,
    TopMenuSvgSpriteComponent,
    NotificationDialogComponent,
    MatButtonModule,
    MatMenuModule,
    MatTooltipModule,
    GlobalAiIconSvgComponent,
    CloseIconSvgComponent,
  ],
})
export class TopMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  private store = inject<Store<AppState>>(Store);
  private afAuth = inject(Auth);
  private router = inject(Router);
  private loading = inject(LoadingService);
  private messages = inject(MessagesService);
  private tenant = inject(TenantService);
  private dialog = inject(MatDialog);
  private uiEvents = inject(UiEventsService);
  private aiChatService = inject(AiGlobalChatService);
  private responsive = inject(ResponsiveLayoutService);
  private elementRef = inject(ElementRef);
  private userNotificationsService = inject(UserNotificationsService);
  private sitePagesService = inject(SitePagesService);
  private userActivityLogService = inject(UserActivityLogService);
  featureFlagService = inject(FeatureFlagService);
  private cookies = inject(CookieService);
  private userPermissions = inject(UserPermissionsService);
  instructorPlanService = inject(InstructorPlanService);

  // ViewChild queries
  readonly navBarLeftMenu = viewChild("navBarLeftMenu", { read: ElementRef });
  readonly navBarRightMenu = viewChild("navBarRightMenu", { read: ElementRef });
  readonly navBarContainer = viewChild('navBarContainer', { read: ElementRef });

  // State signals
  readonly isOpen = signal(false);
  readonly isOpenNotification = signal(false);
  readonly firstExtraMenuEntries = signal<MenuEntry[]>([]);
  readonly moreMenuEntries = signal<MenuEntry[]>([]);

  // Derived state
  readonly dataSig = toSignal(this.store.pipe(select(selectTopMenuData)));
  private readonly tenantInfoSig = toSignal(this.store.pipe(select(selectTenantInfo)));
  readonly isFullScreenMode = toSignal(this.uiEvents.fullScreen$, { initialValue: false });
  readonly isPhonePortrait = toSignal(this.responsive.isMobilePortait$, { initialValue: false });
  readonly customHomePageAvailable = toSignal<CustomPage | undefined>(this.sitePagesService.homePage$, { initialValue: undefined });
  readonly isLandingPageMode = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() =>
        (window.location.search.includes('couponCode') || window.location.search.includes('bundleCode')) &&
        !window.location.search.includes('purchaseResult')
      )
    ),
    { initialValue: false }
  );

  // Set once, not reactive
  readonly isPlatformSite: boolean;
  readonly isSingleSignOnPage: boolean;
  readonly isTenantLoginPage: boolean;
  readonly environment: unknown;

  private resizeObserver: ResizeObserver;
  private resizeTimeout: ReturnType<typeof setTimeout>;

  constructor() {
    this.isPlatformSite = checkIfPlatformSite();
    this.isSingleSignOnPage = checkIfSingleSignOnPage();
    this.isTenantLoginPage = document.location.pathname.endsWith("login-page");
    this.environment = environment;

    // Recalculate responsive menu layout when tenant data or view refs change.
    // scheduleRecalculation() defers via debounce + double-RAF so getBoundingClientRect()
    // is always read after the browser has finished painting, preventing a flicker caused
    // by measuring stale layout during Angular's CD cycle.
    effect(() => {
      const tenant = this.tenantInfoSig();
      const leftMenu = this.navBarLeftMenu();
      const rightMenu = this.navBarRightMenu();

      if (!leftMenu?.nativeElement || !rightMenu?.nativeElement) return;

      // Suppress unused-variable warnings — reads are needed to register reactive deps.
      void tenant;

      this.scheduleRecalculation();
    });

    // Redirect to purchase complete page if a pending purchase session cookie is present
    effect(() => {
      const purchaseSessionId = this.cookies.get("purchaseSessionId");
      if (!purchaseSessionId) return;
      const data = this.dataSig();
      if (data?.isLoggedIn && window.location.pathname !== '/purchasecomplete') {
        window.location.href = "/purchasecomplete";
      }
    });
  }

  async ngOnInit() {
    await this.sitePagesService.getHomePage();
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.scheduleRecalculation());

    const containerEl = this.navBarContainer?.()?.nativeElement;
    const rightEl = this.navBarRightMenu?.()?.nativeElement;

    // Intentionally not observing navBarLeftMenu: its size changes when we add/remove
    // items from it, which would trigger a cascade of recalculations and flicker.
    if (containerEl) this.resizeObserver.observe(containerEl);
    if (rightEl) this.resizeObserver.observe(rightEl);

    requestAnimationFrame(() => this.recalculateMenuEntries());
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  onPopState() {
    this.aiChatService.endGlobalAiChatSession();
  }

  clickout(event: Event) {
    if (!this.elementRef.nativeElement.contains((event as MouseEvent).target)) {
      this.isOpenNotification.set(false);
    }
  }

  onResize() {
    this.scheduleRecalculation();
  }

  private scheduleRecalculation() {
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.recalculateMenuEntries());
      });
    }, 50);
  }

  private layoutMenuEntries(leftEl: HTMLElement, rightEl: HTMLElement, menuEntries: MenuEntry[]) {
    const firstExtraMenuEntries: MenuEntry[] = [];
    const moreMenuEntries: MenuEntry[] = [];

    const leftEdge = leftEl.getBoundingClientRect().right;
    const rightEdge = rightEl.getBoundingClientRect().left;
    let widthAvailable = rightEdge - leftEdge - 50;

    for (const menuEntry of menuEntries) {
      const menuEntryWidth = (menuEntry?.title?.length ?? 10) * 8 + 40;
      if (widthAvailable > menuEntryWidth) {
        firstExtraMenuEntries.push(menuEntry);
        widthAvailable -= menuEntryWidth;
      } else {
        moreMenuEntries.push(menuEntry);
      }
    }

    this.firstExtraMenuEntries.set(firstExtraMenuEntries);
    this.moreMenuEntries.set(moreMenuEntries);
  }

  private recalculateMenuEntries() {
    const leftEl = this.navBarLeftMenu?.()?.nativeElement;
    const rightEl = this.navBarRightMenu?.()?.nativeElement;

    if (!leftEl || !rightEl) return;

    this.layoutMenuEntries(leftEl, rightEl, this.tenantInfoSig()?.customMenu ?? []);
  }

  toggleMenu() {
    this.isOpen.update(v => !v);
  }

  login(startInCoursesPage = false, _tenantInfo: TenantInfo) {
    if (document.location.pathname?.endsWith("login-page")) {
      return;
    }
    goToLoginPage(this.router, startInCoursesPage, this.tenant.id);
  }

  async logout(_tenantInfo: TenantInfo, userId: string) {
    await this.userActivityLogService.reportUserLogout(userId, this.tenant.id);
    this.afAuth.signOut().then(() => {
      this.loading.loadingOn();
      window.location.href = "/courses";
    });
  }

  async toggleNotificationDialog(userId: string, unreadNotificationsCounter: number) {
    try {
      this.isOpenNotification.update(v => !v);

      if (this.isOpenNotification() && unreadNotificationsCounter > 0) {
        await this.userNotificationsService.zeroOutNotificationsCounter(userId);
        this.store.dispatch(userDetailsUpdated({ userId, changes: { unreadNotificationsCounter: 0 } }));
      }
    } catch (e) {
      this.isOpenNotification.update(v => !v);
      this.messages.error(e);
    }
  }

  showNewsletterPopup() {
    const dialogConfig = createDefaultDialogConfig();
    dialogConfig.width = "710px";
    this.dialog.open(NewsletterDialogComponent, dialogConfig);
  }

  joinPartnerProgram() {
    window.open("https://instructor-academy.onlinecoursehost.com/partner-program/", "_blank");
  }

  openHelpCenter() {
    window.open("https://help.onlinecoursehost.com", "_blank");
  }

  closeAiGlobalChat() {
    this.aiChatService.endGlobalAiChatSession();
  }

  toggleAiChat(event: Event, tenant: TenantInfo) {
    if (tenant?.instructorPricingPlan == "growth") {
      this.messages.info(`The AI Course Creation Assistant is only available if you have a paid plan.`);
      return;
    }
    this.aiChatService.toggleAiChat();
    event.stopPropagation();
  }

  isAdminMenuAllowed(data: TopMenuData) {
    return (
      data.permissions.isAdmin ||
      data.permissions.isPlatformAdmin ||
      this.userPermissions.can('Admin') ||
      data.permissions.isCourseAdmin
    );
  }

  showOnBoardingTutorials(data: TopMenuData) {
    return data.permissions.isPlatformAdmin || data.permissions.isAdmin;
  }

  isCommunityVisible(data: TopMenuData) {
    if (!environment?.features?.community) {
      return false;
    }
    if (data.user?.communityMemberSettings?.banned) {
      return false;
    }
    if (data?.permissions?.isPlatformAdmin) {
      return true;
    }
    if (data?.permissions?.isAdmin || data?.permissions?.isCourseAdmin) {
      return true;
    }
    // if it's a student user and a community is configured (paid or free) and not explicitly disabled
    if (
      !data?.permissions?.isAdmin &&
      !data?.permissions?.isCourseAdmin &&
      data?.tenant?.communitySettings?.communityAccess &&
      data?.tenant?.communitySettings?.disabled !== true
    ) {
      return true;
    }
    if (
      isAnonymousUser(data?.user) &&
      data?.tenant?.communitySettings?.communityAccess == "free" &&
      data?.tenant?.communitySettings?.disabled == false
    ) {
      return true;
    }
    return false;
  }

  isPageBuilderVisible(data: TopMenuData) {
    const active = environment?.features?.pageBuilder || data?.permissions?.isPlatformAdmin;
    const hasPermission = this.userPermissions.can(PermissionKeys.PageBuilder);
    return active && (data?.permissions?.isAdmin || hasPermission);
  }

  isUpgradeButtonVisible(data: TopMenuData) {
    if (isAnonymousUser(data.user)) {
      return false;
    }
    if (!data.permissions.isAdmin) {
      return false;
    }
    if (data.tenant?.instructorPricingPlan == "unlimited") {
      return false;
    }
    if (data.tenant?.instructorPricingPlan == "growth") {
      return false;
    }
    return environment?.features?.upgradeButton;
  }

  async onUpgradeBtnClicked(data: TopMenuData) {
    if (isLifetimeCustomer(data.tenant)) {
      await this.router.navigateByUrl("/upgrade-to-unlimited");
    } else {
      createInstructorPlanDialog(this.dialog)
        .pipe(
          concatMap(plan => this.loading.showLoaderUntilCompleted(
            this.instructorPlanService.updateInstructorPlan(
              false,
              this.tenant.tenant['email'],
              plan?.selectedInstructorPlanId,
              plan?.selectedPlanFrequency
            )
          )),
          tap(async (response) => {
            const StripeConstructor = await loadStripeJs();
            const stripe = StripeConstructor(response.stripePublicKey);
            stripe.redirectToCheckout({ sessionId: response.sessionId });
          })
        )
        .subscribe();
    }
  }

  closeNotificationDialog() {
    this.isOpenNotification.set(false);
  }

  evaluateCustomMenuHref(customMenu: MenuEntry) {
    if (customMenu?.['dynamic']) {
      if (customMenu?.link) {
        const { pathname } = new URL(customMenu.link);
        const origin = location.origin;
        if (pathname.split('/').filter(Boolean)[0] === 'pages') {
          return origin + pathname;
        }
      }
    }
    return customMenu?.link;
  }

  adminMenuLink(data: TopMenuData): string {
    if (data.permissions.isCourseAdmin) {
      return '/admin/settings/manage-students';
    }
    return this.userPermissions.redirectUserAccordingToPermissions();
  }
}
